import styled from '@emotion/styled'
import {
  GAM_DASHBOARD_NOT_APPLICABLE_AB_TEST,
  GAM_DASHBOARD_NOT_APPLICABLE_AD_UNIT_LEVEL,
  GAM_DASHBOARD_OTHERS,
  GAM_DASHBOARD_UNMAPPED_ADUNIT,
  GAM_DASHBOARD_UNMAPPED_SITE,
  GamOverviewMetricsByTime,
} from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { DateTime } from 'luxon'
import { FunctionComponent, useState } from 'react'
import { Color } from '~/assets/style/colors'
import { ChartTooltipMetricProps, generateChartTooltip } from '~/components/ChartTooltip'
import { LegendProps } from '~/components/Legend'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { TabProp, Tabs } from '~/components/Tabs'
import { TimelineMetricProps } from '~/components/TimelineMetric'
import { WidgetProps } from '~/components/Widget'
import { AnalyticsDefaultChartOptions } from '~/modules/analytics/AnalyticsCharts'
import { AnalyticsChartWidget } from '~/modules/analytics/AnalyticsChartWidget'
import { eCPMObj, impressionCountObj, impressionRevenueObj, videoCompletionRate, videoViewershipTotalErrorCount, viewabilityMRCObj } from '~/modules/analytics/common/formulas'
import { DATA_TYPES, getSortedKeysByImpression } from '~/modules/analytics/common/shared'
import { WithClassName } from '~/types/utils'
import { CHART_DATE_FORMAT_NO_HOUR } from '~/utils/analytics'
import { ANALYTICS_TOOLTIPS } from '~/utils/constants'
import { GAM_OVERVIEW_DIMENSION_COLORS, GAM_OVERVIEW_GENERAL_COLOR, GamOverviewDimensions } from '~/utils/gamOverview'
import { aggregateRawDataMapping, aggregateRawDataValues } from '~/utils/object'

type PureGamOverviewTimelineProps = WithClassName &
  Omit<WidgetProps, 'title' | 'info' | 'icon' | 'refreshTrackingLabel'> & {
    rawData: GamOverviewMetricsByTime
    currencySymbol: CurrencySymbol
    onMetricChange: (metric: string) => void
    dimension: GamOverviewDimensions
    displayVideoData: boolean
  }
type GamOverviewFormulas = typeof impressionRevenueObj | typeof impressionCountObj | typeof eCPMObj | typeof viewabilityMRCObj | typeof videoCompletionRate | typeof videoViewershipTotalErrorCount

type GamOverviewMetric = {
  name: string
  formula: GamOverviewFormulas
  seriesType: 'bars' | 'line'
  tooltipText?: keyof typeof ANALYTICS_TOOLTIPS
  percentage: boolean
  isVideo?: boolean
}

type InputDataToConvertForChart = {
  rawData: GamOverviewMetricsByTime
  currentFormula: GamOverviewMetric
  chartDataPoints: string[]
  currencySymbol: CurrencySymbol
  colors: { [key: string]: Color }
  type: 'percentage' | 'number'
}
const convertData = ({ rawData, currentFormula, chartDataPoints, currencySymbol, colors, type }: InputDataToConvertForChart) => {
  if (!rawData) return []

  return rawData.epoch.map((epoch, index) => {
    const result: (string | Date | number)[] = [
      DateTime.fromISO(epoch).toFormat('LLLL dd, yyyy'),
      getChartTooltip(DateTime.fromISO(epoch), currentFormula, rawData, index, chartDataPoints, currencySymbol, colors, type),
      ...Object.entries((type === 'percentage' ? currentFormula.formula.percentage : currentFormula.formula.compute)(rawData as any, index))
        .sort((a, b) => chartDataPoints.indexOf(a[0]) - chartDataPoints.indexOf(b[0]))
        .map((e) => e[1]),
    ]
    return result
  })
}

const aggregateRawData = (rawData: GamOverviewMetricsByTime): GamOverviewMetricsByTime => {
  const keysToAggregate = getSortedKeysByImpression<GamOverviewMetricsByTime>(rawData, GAM_DASHBOARD_OTHERS)

  let clonedData = JSON.parse(JSON.stringify(rawData)) // TODO 2022-08-26 NRA VMA check to improve type
  clonedData = Object.entries<{ [key: string]: number }[]>(clonedData)
    .filter((entry) => entry[0] !== 'epoch' && entry[0] !== 'mappings')
    .reduce((acc, [currentKey, currentValue]) => {
      const metricsValues = currentValue.map((v) => aggregateRawDataValues(v, keysToAggregate, GAM_DASHBOARD_OTHERS))
      return { ...acc, [currentKey]: metricsValues }
    }, {})
  clonedData.mappings = aggregateRawDataMapping(rawData.mappings, keysToAggregate, GAM_DASHBOARD_OTHERS)
  clonedData.epoch = [...rawData.epoch]
  return clonedData
}

const getChartTooltip = (
  epoch: DateTime,
  metricConfig: GamOverviewMetric,
  rawData: GamOverviewMetricsByTime,
  index: number,
  chartDataPoints: string[],
  currencySymbol: CurrencySymbol,
  colors: { [p: string]: Color },
  type: 'percentage' | 'number'
) => {
  const chartData = (type === 'percentage' ? metricConfig.formula.percentage : metricConfig.formula.compute)(rawData as any, index)
  const total = metricConfig.formula.sum(rawData as any, index)
  const metrics: ChartTooltipMetricProps[][] = [
    chartDataPoints.map((label) => ({
      label: rawData.mappings[label],
      value: (type === 'percentage' ? metricConfig.formula.displayablePercentage : metricConfig.formula.displayable)(chartData[label], currencySymbol).toString(),
      iconColor: colors[label],
    })),
    [{ label: metricConfig.formula.name, value: metricConfig.formula.displayable(total, currencySymbol).toString(), iconName: 'sigma' }],
  ]
  return generateChartTooltip({ date: epoch.toFormat(CHART_DATE_FORMAT_NO_HOUR), metrics })
}

const metricsFormulas: GamOverviewMetric[] = [
  {
    name: 'REVENUE',
    formula: impressionRevenueObj,
    seriesType: 'bars',
    percentage: true,
    tooltipText: 'GAM_REVENUE',
  },
  {
    name: 'IMPRESSION_COUNT',
    formula: impressionCountObj,
    seriesType: 'bars',
    percentage: true,
  },
  {
    name: 'ECPM',
    formula: eCPMObj,
    seriesType: 'line',
    percentage: false,
    tooltipText: 'ECPM',
  },
  {
    name: 'VIEWABILITY',
    formula: viewabilityMRCObj,
    seriesType: 'line',
    percentage: false,
    tooltipText: 'VIEWABILITY',
  },
  {
    name: 'VIDEO_COMPLETION_RATE',
    formula: videoCompletionRate,
    seriesType: 'line',
    percentage: false,
    tooltipText: 'VIDEO_COMPLETION_RATE',
    isVideo: true,
  },
  {
    name: 'VIDEO_TOTAL_ERROR_COUNT',
    formula: videoViewershipTotalErrorCount,
    seriesType: 'line',
    percentage: false,
    tooltipText: 'VIDEO_ERROR_RATE',
    isVideo: true,
  },
]

const UnstyledPureGamOverviewTimeline: FunctionComponent<PureGamOverviewTimelineProps> = ({ rawData, onMetricChange, currencySymbol, dimension, displayVideoData, ...props }) => {
  const data: GamOverviewMetricsByTime = aggregateRawData(rawData)
  const [currentMetricIndex, setCurrentMetricIndex] = useState<number>(0)
  const [typeTab, setTypeTab] = useState(0)
  const type = DATA_TYPES[typeTab].type
  const onTabClick = (tab: TabProp) => {
    const index = tabs.indexOf(tab)
    setTypeTab(index)
  }

  const currentMetric = metricsFormulas[currentMetricIndex]
  const tabs: TabProp[] = DATA_TYPES.map((type, index) => ({
    label: type.label,
    active: typeTab === index,
    disabled: type.type === 'percentage' && !currentMetric.percentage,
    trackingLabel: `ana-gam-overview-timeline-${type.type}-tab-apply`,
  }))

  const metrics: TimelineMetricProps[] = metricsFormulas.map((metric, index) => ({
    active: currentMetric.formula.name === metric.formula.name,
    label: metric.formula.name,
    value: `${metric.formula.displayable(metric.formula.sum(data as any), currencySymbol)}`,
    notApplicable: !metric.formula.isComputable(data) || (metric.isVideo && !displayVideoData),
    onClick: () => {
      onMetricChange && onMetricChange(metric.name)
      if (!metric.percentage) {
        setTypeTab(0)
      }
      ;((!metric.isVideo && metric.formula.isComputable(data)) || displayVideoData) && setCurrentMetricIndex(index)
    },
    trackingLabel: `ana-gam-overview-timeline-metric-${normalizeAsTrackingLabel(metric.formula.name)}-apply`,
    tooltipText: metric.tooltipText ? ANALYTICS_TOOLTIPS[metric.tooltipText] : undefined,
  }))

  const chartDataPoints = getSortedKeysByImpression(data, GAM_DASHBOARD_OTHERS)
  const colors = chartDataPoints
    .filter((d) => d !== GAM_DASHBOARD_OTHERS && d !== GAM_DASHBOARD_UNMAPPED_SITE && d !== GAM_DASHBOARD_UNMAPPED_ADUNIT && d !== GAM_DASHBOARD_NOT_APPLICABLE_AB_TEST)
    .reduce<{ [key: string]: Color }>((acc, currentValue, index) => {
      return {
        ...acc,
        [currentValue]:
          (['pubstackDemandChannel', 'device'].includes(dimension) ? GAM_OVERVIEW_DIMENSION_COLORS[dimension][currentValue] : GAM_OVERVIEW_DIMENSION_COLORS[dimension][index]) ||
          GAM_OVERVIEW_GENERAL_COLOR.NotApplicable, // default color (safeguard)
      }
    }, {})
  if (data.mappings[GAM_DASHBOARD_OTHERS]) {
    colors[GAM_DASHBOARD_OTHERS] = GAM_OVERVIEW_GENERAL_COLOR.Others
  }
  if (data.mappings[GAM_DASHBOARD_UNMAPPED_ADUNIT]) {
    colors[GAM_DASHBOARD_UNMAPPED_ADUNIT] = GAM_OVERVIEW_GENERAL_COLOR.NotApplicable
  }
  if (data.mappings[GAM_DASHBOARD_UNMAPPED_SITE]) {
    colors[GAM_DASHBOARD_UNMAPPED_SITE] = GAM_OVERVIEW_GENERAL_COLOR.NotApplicable
  }
  if (data.mappings[GAM_DASHBOARD_NOT_APPLICABLE_AD_UNIT_LEVEL]) {
    colors[GAM_DASHBOARD_NOT_APPLICABLE_AD_UNIT_LEVEL] = GAM_OVERVIEW_GENERAL_COLOR.NotApplicable
  }
  if (data.mappings[GAM_DASHBOARD_NOT_APPLICABLE_AB_TEST]) {
    colors[GAM_DASHBOARD_NOT_APPLICABLE_AB_TEST] = GAM_OVERVIEW_GENERAL_COLOR.NotApplicable
  }

  const chartOptions = {
    ...AnalyticsDefaultChartOptions,
    colors: chartDataPoints.map((i) => colors[i]),
    seriesType: currentMetric.seriesType,
    isStacked: true,
    vAxis: type === 'percentage' ? { ...AnalyticsDefaultChartOptions.vAxis, format: "#'%'", minValue: 0 } : { ...AnalyticsDefaultChartOptions.vAxis },
  }

  const chartData = [
    [
      'Date',
      {
        type: 'string',
        role: 'tooltip',
        p: { html: true },
      },
      ...chartDataPoints,
    ],
    ...convertData({ rawData: data, currentFormula: currentMetric, chartDataPoints, currencySymbol, colors, type }),
  ]
  const legends: LegendProps[] = chartDataPoints.map((k) => ({ iconColor: colors[k], label: data.mappings[k] }))

  return (
    <AnalyticsChartWidget
      {...props}
      icon={'chart_bar'}
      title={'Performance'}
      metrics={metrics}
      legends={legends}
      chart={{ chartType: 'ComboChart', options: chartOptions, data: rawData ? chartData : undefined }}
      info={<Tabs tabs={tabs} fluid={false} onClick={onTabClick} />}
      refreshTrackingLabel={'ana-gam-overview-timeline-refresh'}
    />
  )
}

export const PureGamOverviewTimeline = styled(UnstyledPureGamOverviewTimeline)``
